import {
  Body,
  CustomLink,
  Flex,
  LazyImage,
  Modal,
  Shape,
  Title,
  styled,
} from '@teko/ds-desktop';
import { YoutubeFrame } from 'components/YoutubeFrame';
import { useProductDetailState } from 'contexts';
import { t } from 'i18n';
import { SeeMore } from 'modules/search-page/mobile/SGC';
import React, { useMemo, useState } from 'react';

const MAX_ITEMS = 3;

type TabKey = 'video' | 'post';

type NewsItem = { url: string; thumbnailUrl: string; description: string };

export const ProductNews: React.FC = () => {
  const { product } = useProductDetailState();
  const { relatedPosts, relatedVideos } = product?.seoInfo || {
    relatedPosts: { title: '' },
    relatedVideos: { title: '' },
  };

  const videoItems: NewsItem[] = useMemo(
    () =>
      (relatedVideos?.urls || [])
        .slice(0, MAX_ITEMS)
        .map(({ url, thumbnailUrl, name }) => ({
          url: url || '',
          thumbnailUrl: thumbnailUrl || '',
          description: name || '',
        })),
    [relatedVideos],
  );

  const postItems: NewsItem[] = useMemo(
    () =>
      (relatedPosts?.urls || [])
        .slice(0, MAX_ITEMS)
        .map(({ url, thumbnailUrl, description }) => ({
          url: url || '',
          thumbnailUrl: thumbnailUrl || '',
          description: description || '',
        })),
    [relatedPosts],
  );

  const hasVideos = !!videoItems.length;
  const hasPosts = !!postItems.length;

  const tabs = useMemo(
    () =>
      [
        hasPosts && { key: 'post' as TabKey, label: t('Bài viết liên quan') },
        hasVideos && { key: 'video' as TabKey, label: t('Video liên quan') },
      ].filter(Boolean) as Array<{ key: TabKey; label: string }>,
    [hasPosts, hasVideos],
  );

  const [activeTab, setActiveTab] = useState<TabKey>(
    hasPosts ? 'post' : 'video',
  );
  const [youtubeLink, setYoutubeLink] = useState<string>();

  if (!hasVideos && !hasPosts) return null;

  // Guard against a stale activeTab (e.g. client navigation to a page that no
  // longer has the previously-selected tab) by falling back to the first tab.
  const currentTab = tabs.some((tab) => tab.key === activeTab)
    ? activeTab
    : tabs[0].key;
  const isPost = currentTab === 'post';
  const items = isPost ? postItems : videoItems;
  const seeMoreUrl = isPost
    ? relatedPosts?.seeMoreUrl
    : relatedVideos?.seeMoreUrl;

  const renderItem = (item: NewsItem) => (
    <Flex gap="s16" align="start">
      <StyledThumbnail
        src={item.thumbnailUrl}
        width="200px"
        height="100px"
        borderRadius={8}
        cover
        imageWidth={400}
      />
      <Shape width="calc(100% - 200px - 12px)">
        <Body level={1} line={3} color="black">
          {item.description}
        </Body>
      </Shape>
    </Flex>
  );

  return (
    <Flex direction="column" backgroundColor="white" borderRadius="xl">
      <Content>
        <Title level={3} color="black">
          {t('Tin tức sản phẩm')}
        </Title>
        <Flex gap="s8" margin={['s16', 's0']}>
          {tabs.map((tab) => (
            <PillTab
              key={tab.key}
              isActive={currentTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            >
              <SMNormal color={currentTab === tab.key ? 'primary' : 'black'}>
                {tab.label}
              </SMNormal>
            </PillTab>
          ))}
        </Flex>
        <ItemList gap="s16" direction="column">
          {items.map((item, index) =>
            isPost ? (
              <StyledCustomLink
                key={`${index}${item.url}`}
                href={item.url}
                target="_blank"
              >
                {renderItem(item)}
              </StyledCustomLink>
            ) : (
              <Shape
                key={`${index}${item.url}`}
                style={{ cursor: 'pointer', width: '100%' }}
                onClick={() => setYoutubeLink(item.url)}
              >
                {renderItem(item)}
              </Shape>
            ),
          )}
        </ItemList>
      </Content>
      {!!seeMoreUrl && <SeeMore href={seeMoreUrl}>{t('Xem thêm')}</SeeMore>}

      <Modal
        visible={!!youtubeLink}
        width="960px"
        onClose={() => setYoutubeLink('')}
        showCloseIcon={false}
        footer={false}
        contentStyle={{ padding: '0px' }}
      >
        <IFramePopupWrapper>
          <YoutubeFrame youtubeLink={youtubeLink} />
        </IFramePopupWrapper>
      </Modal>
    </Flex>
  );
};

const PillTab = styled.div<{ isActive: boolean }>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 8px;
  border-radius: 999px;
  border: 1px solid
    ${({ theme, isActive }) =>
      isActive ? theme.colors.primary : theme.colors.divider};
  background: ${({ isActive }) => (isActive ? '#0200BC1A' : 'transparent')};
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
`;

const Content = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 16px;
`;

// Item list scrolls internally so the title/tabs and pinned "Xem thêm" stay visible
// even when items exceed the card's max height (overflow-x hidden avoids a stray
// horizontal scrollbar that overflow-y:auto would otherwise force).
const ItemList = styled(Flex)`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

const StyledThumbnail = styled(LazyImage)`
  flex: 0 0 200px;
`;

const StyledCustomLink = styled(CustomLink)`
  width: 100%;
`;

const IFramePopupWrapper = styled.div`
  width: 100%;
  height: 540px;
  background-color: black;
`;

const SMNormal = styled(Body)`
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
`;
